import React, { useState } from 'react';
import { Row, Card, Form, Input, Typography, Button, message } from 'antd';
import { Auth } from 'aws-amplify';
import logo from '../../images/loginLogo.svg';

const { Title, Text, Link } = Typography;
const { Password } = Input;

interface IProps {
  authState?: any;
  onStateChange?: any;
}

const SignIn: React.FC<IProps> = (props: IProps) => {
  const { authState, onStateChange } = props;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const validAuthState = ['signIn', 'signOut', 'signedUp', 'signedIn'].includes(
    authState,
  );

  /*
   * When the form passes client side validation, we sign the user in and
   * check to see if the CognitoUser in the response contains and SMS_MFA
   * challenge. If yes, we change authState to confirmSignIn and pass along
   * the CongnitoUser through the authData.
   *
   * If the response contains a NEW_PASSWORD_REQUIRED challenge, we change
   * authState to requireNewPassword and pass along the Cognito User through
   * the authData.
   *
   * On an error, we reset the form fields, set the loading state to
   * false and display the error message to the user.
   *
   * If the error requires a password reset for the user, we update
   * authState to forgotPassword.
   */

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const user = await Auth.signIn(values.username, values.password);

      if (user.challengeName === 'SMS_MFA') {
        onStateChange('confirmSignIn', user);
      } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        onStateChange('requireNewPassword', user);
        message.warn('Please change your password!');
      } else {
        form.resetFields();
        message.error('Looks like something went wrong!');
      }

      setLoading(false);
    } catch (e) {
      form.resetFields();
      setLoading(false);

      if (e.code === 'PasswordResetRequiredException') {
        onStateChange('forgotPassword');
      }

      message.error('Unauthorized.');
    }
  };

  const onForgotClick = () => {
    onStateChange('forgotPassword');
  };

  return validAuthState ? (
    <Row justify="center" align="middle" className="Authenticator-page">
      <Card className="Authenticator-card Authenticator-card-fade-in">
        <img src={logo} alt="ehe-logo" className="Authenticator-logo" />
        <Form form={form} size="large" onFinish={onFinish}>
          <Title level={4}>Username or Email:</Title>
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: 'Please enter your username!',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Title level={4}>Password:</Title>
          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: 'Please enter your password!',
              },
            ]}
          >
            <Password />
          </Form.Item>
          <Row justify="end">
            <Link onClick={onForgotClick}>Forgot Password?</Link>
          </Row>
          <Row justify="center" className="Authenticator-margin-top">
            <Button type="primary" htmlType="submit" loading={loading}>
              Log In
            </Button>
          </Row>
          <Row justify="center" className="Authenticator-margin-top">
            <Text>
              Don&apos;t have an account?{' '}
              <Link href="mailto:ehe-tracker@icf.com">Request a login</Link>
            </Text>
          </Row>
        </Form>
      </Card>
    </Row>
  ) : null;
};

export default SignIn;
